import { RowClassRules } from 'ag-grid-community'

// 行コピー/切り取り対象行の破線枠（agGrid.scss の .ag-row-copied-*-row）用の共通実装。
// 対象行は gridOptions.context.copied（CopiedRow[]）に保持し、rowClassRules が
// 位置クラスを付与する。BulkSheet / BulkSheetView の両コンテナから共通利用する。

export type ClipboardPlace = 'top' | 'middle' | 'bottom' | 'both'
export type CopiedRow = {
  id: string
  place: ClipboardPlace
  cut?: boolean
}

// コピー対象行それぞれについて、連続コピーのブロック内でどの位置か
// （先頭=top/中=middle/末尾=bottom/単独=both）をデータ順で判定する。
export const copyRows = <R extends { uuid: string }>(
  allRows: R[],
  copiedRows: R[],
  cut?: boolean
): CopiedRow[] => {
  const copied: CopiedRow[] = []
  if (copiedRows.length === 1) {
    return [{ id: copiedRows[0].uuid, place: 'both', cut }]
  }

  const copiedRowsUuids = new Set(copiedRows.map(row => row.uuid))
  copiedRowsUuids.forEach(uuid => {
    const i = allRows.findIndex(v => v.uuid === uuid)
    if (i < 0) return
    const data = allRows[i]
    const previous = i > 0 ? allRows[i - 1] : undefined
    const previousCopied = previous && copiedRowsUuids.has(previous.uuid)
    const following = i < allRows.length - 1 ? allRows[i + 1] : undefined
    const followingCopied = following && copiedRowsUuids.has(following.uuid)
    if (previousCopied && followingCopied) {
      copied.push({ id: data.uuid, place: 'middle', cut })
      return
    }
    if (previousCopied) {
      copied.push({ id: data.uuid, place: 'bottom', cut })
      return
    }
    if (followingCopied) {
      copied.push({ id: data.uuid, place: 'top', cut })
      return
    }
    copied.push({ id: data.uuid, place: 'both', cut })
  })
  return copied
}

// コピー/切り取り対象行に破線枠の位置クラスを付与する rowClassRules。
// クラス変更を反映するには、対象行を api.redrawRows で再描画すること。
export const COPIED_ROW_CLASS_RULES: RowClassRules = {
  'ag-row-copied-or-selected-row': p =>
    p.context.copied?.some(v => v.id === p.node.id),
  'ag-row-copied-single-row': p =>
    p.context.copied?.some(v => v.id === p.node.id && v.place === 'both'),
  'ag-row-copied-start-row': p =>
    p.context.copied?.some(v => v.id === p.node.id && v.place === 'top'),
  'ag-row-copied-middle-row': p =>
    p.context.copied?.some(v => v.id === p.node.id && v.place === 'middle'),
  'ag-row-copied-end-row': p =>
    p.context.copied?.some(v => v.id === p.node.id && v.place === 'bottom'),
}
